// Captures a dashboard thumbnail for a just-promoted app by loading it in a
// hidden iframe (src=, genuinely cross-origin from Studio — same as the live
// preview iframe in PreviewPane.js, NOT srcdoc/same-origin) and asking the
// app's own already-injected script (makeScreenshotScript in
// templates.js) to render + downscale itself via html2canvas and hand the
// result back via postMessage.
//
// Why not render it ourselves (html2canvas from Studio's own JS against the
// iframe's DOM)? That requires the iframe to be same-origin to Studio
// (srcdoc + sandbox="allow-same-origin"), which then also requires
// allow-scripts so the app's own data-fetching JS can run and actually
// populate the page before capture (otherwise every thumbnail is just the
// initial "Loading…" splash screen — see the git history of this file for
// that exact bug). But allow-scripts + allow-same-origin together means the
// app's JS executes WITH Studio's origin: access to Studio's cookies,
// localStorage, and window.parent. The live preview iframe never grants
// that — it's a real cross-origin src= load. Loading the SAME app here via
// src= instead of srcdoc keeps that isolation intact: the app's JS runs at
// its own origin, exactly as it does in preview and on real devices, with
// zero access to anything Studio-side. The tradeoff is Studio can't read
// this iframe's DOM directly (genuinely cross-origin) — which is exactly
// why the app renders + captures itself instead, and hands back only the
// finished image bytes via postMessage (a cross-origin-safe channel — it's
// message-passing, not a DOM read).
//
// Returns a base64 JPEG string (no `data:` prefix) on success, or null on
// any failure or timeout. Callers must treat null as "no thumbnail this
// time" — this must never block or fail a promote.
//
// timeoutMs bounds the WHOLE round trip: iframe load + the injected script's
// own wait for its data to actually render (up to ~8s — see the
// forge-capture-now handler in templates.js: a 6s fallback plus a 2s settle
// delay) + html2canvas's own CDN load/render time + postMessage back.
// Default has headroom above that worst case.
export async function captureThumbnail(url, { width = 1080, height = 1920, timeoutMs = 15000 } = {}) {
  if (!url) return null;

  let targetOrigin;
  try {
    targetOrigin = new URL(url).origin;
  } catch {
    return null;
  }

  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.top = "-99999px";
  frame.style.left = "-99999px";
  frame.style.width = `${width}px`;
  frame.style.height = `${height}px`;
  frame.src = url;
  document.body.appendChild(frame);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
      frame.remove();
      resolve(result);
    };

    // Origin-checked both ways: only accept a response from the exact
    // window we created (ev.source === frame.contentWindow), from the
    // origin we actually loaded (ev.origin === targetOrigin) — a page
    // loaded into this iframe couldn't be anything else by the time it
    // responds, but checking both costs nothing and rules out any
    // possibility of a stray message from elsewhere being mistaken for
    // this capture's response.
    const onMessage = (ev) => {
      if (ev.source !== frame.contentWindow) return;
      if (ev.origin !== targetOrigin) return;
      if (!ev.data || ev.data.type !== "forge-capture-done") return;
      const image = ev.data.image;
      finish(typeof image === "string" ? image.split(",")[1] || null : null);
    };
    window.addEventListener("message", onMessage);

    const timer = setTimeout(() => finish(null), timeoutMs);

    frame.addEventListener(
      "load",
      () => {
        try {
          frame.contentWindow.postMessage({ type: "forge-capture-now" }, targetOrigin);
        } catch {
          finish(null);
        }
      },
      { once: true }
    );
    frame.addEventListener("error", () => finish(null), { once: true });
  });
}
